import React, { useMemo } from 'react';
import { Container, Row, Col } from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import { CellContext } from '@tanstack/react-table';
import Breadcrumbs from 'components/Common/Breadcrumb';
import { DataTable } from 'components/DataTable';
import { DataTableColumn, TableQueryParams, PaginatedResponse } from 'types/table.types';
import { SHOP_PATHS } from 'routes/routesConstants';
import { useShop } from 'context/shopContext';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { DataTableApiService } from 'services/api/tableApi';
import { formatRelativeDate } from 'utils/dateFormatter';

interface ShopItem {
  id: string;
  sku: string;
  name: string;
  description: string;
  imageUrl: string | null;
  isActive: boolean;
  requiresPrescription: boolean;
  severity: string;
  updatedAt: string;
}

const PLACEHOLDER_IMAGE = 'https://placehold.co/60x60.png?text=No+Image';

const MedicineItemList: React.FC = () => {
  const { shop } = useShop();
  const { id: shopId } = shop!;
  const navigate = useNavigate();

  /**
   * Define columns for the data table
   */
  const columns = useMemo<DataTableColumn<ShopItem>[]>(
    () => [
      {
        accessorKey: 'imageUrl',
        id: 'imageUrl',
        header: 'Image',
        enableSorting: false,
        enableFiltering: false,
        cell: (info: CellContext<ShopItem, unknown>) => (
          <img
            src={(info.getValue() as string) || PLACEHOLDER_IMAGE}
            alt="Medicine"
            style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }}
          />
        ),
      },
      {
        accessorKey: 'name',
        id: 'name',
        header: 'Name',
        enableSorting: true,
        enableFiltering: true,
        cell: (info: CellContext<ShopItem, unknown>) => (
          <Link to={`/medicines/${(info.row as any).original.id}`} className="text-dark fw-bold">
            {info.getValue() as string}
          </Link>
        ),
      },
      {
        accessorKey: 'sku',
        id: 'sku',
        header: 'SKU',
        enableSorting: true,
        enableFiltering: true,
        cell: (info: CellContext<ShopItem, unknown>) => <code>{info.getValue() as string}</code>,
      },
      {
        accessorKey: 'description',
        id: 'description',
        header: 'Description',
        enableSorting: false,
        enableFiltering: true,
        cell: (info: CellContext<ShopItem, unknown>) => {
          const desc = info.getValue() as string;
          return <small>{desc?.substring(0, 50)}...</small>;
        },
      },
      {
        accessorKey: 'requiresPrescription',
        id: 'requiresPrescription',
        header: 'Requires Prescription',
        enableSorting: true,
        enableFiltering: true,
        filterType: 'checkbox',
        cell: (info: CellContext<ShopItem, unknown>) => (
          <span className="badge bg-info">{(info.getValue() as boolean) ? 'Yes' : 'No'}</span>
        ),
      },
      {
        accessorKey: 'severity',
        id: 'severity',
        header: 'Severity',
        enableSorting: true,
        enableFiltering: true,
        filterType: 'select',
        filterOptions: [
          { label: 'Low', value: 'low' },
          { label: 'Medium', value: 'medium' },
          { label: 'High', value: 'high' },
        ],
        cell: (info: CellContext<ShopItem, unknown>) => {
          const severity = (info.getValue() as string)?.toLowerCase();
          const colors: Record<string, string> = {
            low: 'bg-success',
            medium: 'bg-warning',
            high: 'bg-danger',
          };
          return (
            <span className={`badge ${colors[severity] || 'bg-secondary'}`}>
              {info.getValue() as string}
            </span>
          );
        },
      },
      {
        accessorKey: 'updatedAt',
        id: 'updatedAt',
        header: 'Last Updated',
        enableSorting: true,
        enableFiltering: false,
        cell: (info: CellContext<ShopItem, unknown>) => {
          const { text, isStale } = formatRelativeDate(info.getValue() as string);
          return <span className={`small ${isStale ? 'text-danger' : 'text-muted'}`}>{text}</span>;
        },
      },
      {
        accessorKey: 'actions',
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        enableFiltering: false,
        cell: (info: CellContext<ShopItem, unknown>) => {
          const row = (info.row as any).original;
          return (
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-primary"
                onClick={() =>
                  navigate(
                    SHOP_PATHS.EDIT_ITEM.replace(':shopId', shopId || '').replace(':id', row.id),
                  )
                }
              >
                <i className="ri-edit-line"></i> Edit
              </button>
              <button
                className="btn btn-sm btn-danger"
                onClick={() => {
                  if (window.confirm(`Delete item "${row.name}"?`)) {
                    console.log('Delete item:', row.id);
                    // TODO: Implement delete
                  }
                }}
              >
                <i className="ri-delete-bin-line"></i> Delete
              </button>
            </div>
          );
        },
      },
    ],
    [shopId, navigate],
  );

  /**
   * Fetch handler for server-side data
   */
  const handleFetchData = async (
    params: TableQueryParams,
  ): Promise<PaginatedResponse<ShopItem>> => {
    return DataTableApiService.fetchPaginatedData<ShopItem>(
      API_ENDPOINTS.TENANT.SHOP_ITEMS.BASE,
      params,
    );
  };

  /**
   * Add new medicine handler
   */
  const handleAddMedicine = () => {
    navigate(SHOP_PATHS.ADD_ITEM.replace(':shopId', shopId || ''));
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg="12">
            <div className="mb-3 d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Medicine Inventory</h5>
              <button className="btn btn-success" onClick={handleAddMedicine}>
                <i className="ri-add-line"></i> Add Medicine
              </button>
            </div>

            <DataTable
              columns={columns}
              queryKey={API_ENDPOINTS.TENANT.SHOP_ITEMS.BASE}
              onFetchData={handleFetchData}
              enableSearch={true}
              enableFiltering={true}
              enablePagination={true}
              enableSorting={true}
              enableColumnVisibility={true}
              enableColumnReorder={false}
              enableRowSelection={false}
              enableBulkActions={false}
              enableExport={true}
              pageSizeOptions={[10, 20, 50, 100]}
              defaultPageSize={10}
              columnVisibilityStorageKey="medicine_items_visibility"
              columnOrderStorageKey="medicine_items_order"
              title=""
              emptyMessage="No medicines found. Create one to get started."
              striped={true}
              hover={true}
              bordered={true}
              responsive={true}
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default MedicineItemList;
